import { inject, Pipe, PipeTransform } from '@angular/core';
import { Environment, ENVIRONMENT_TOKEN } from 'account-hybrid/core/environment.service';
import { buildCnameUrl } from 'apps/account/src/account-shared/pipes/cname-url.util';
import { parseValue } from 'apps/account/src/account-shared/pipes/parse-value.util';
import dayjs from 'dayjs/esm/index';
import utc from 'dayjs/esm/plugin/utc';

dayjs.extend(utc);

/**
 * Local standalone `onOff` pipe — verbatim port of the AngularJS
 * `flipto.components.common.textFormatting` `onOff` filter
 * (account-v1/.../text-formatting/onOff.js). Boolean / numeric-string /
 * 'true'-string → 'On' / 'Off'. Used by the invitations + details read-view
 * summary panels; byte-faithful so the candidate text matches the FROZEN v1.
 */
@Pipe({ name: 'onOff' })
export class OnOffPipe implements PipeTransform {
  transform(input: any): string {
    if (typeof input === 'string') {
      if (!isNaN(input as any)) {
        return +input ? 'On' : 'Off';
      }
      return input.toLowerCase() === 'true' ? 'On' : 'Off';
    }
    return input ? 'On' : 'Off';
  }
}

/**
 * Local standalone `cnameUrl` pipe — now NATIVE (was bridged to the AngularJS
 * `$filter('cnameUrl')` via $injector). Delegates to `buildCnameUrl` (the
 * verbatim v1 `cnameUrl` filter port in account-shared) with the property's
 * `FliptoHubDomain`, so it builds the cname-rooted public URLs (invitation
 * permalink / live-preview link) byte-identically to the v1 filter the FROZEN
 * baseline renders — character-for-character (pixel parity), no `$filter` bridge.
 */
@Pipe({ name: 'cnameUrl' })
export class CnameUrlPipe implements PipeTransform {
  private readonly environment = inject<Environment>(ENVIRONMENT_TOKEN);

  transform(subdomains: any, purpose: number, propertySlug?: string, params?: any): string {
    return buildCnameUrl(subdomains, purpose, propertySlug, params, this.environment.FliptoHubDomain);
  }
}

/**
 * Local standalone `parse` pipe — native verbatim port of the v1 `$filter('parse')`
 * (delegates to `parseValue`, a byte-exact copy of core/parse/parse.js; proven 0/900 by
 * _diag-parse-check.mjs) so the contest details read-view matches v1 character-for-character
 * off the $injector bridge. Kept LOCAL + standalone (barrel-free).
 */
@Pipe({ name: 'parse' })
export class ParsePipe implements PipeTransform {
  transform(value: any, ...args: any[]): any {
    return parseValue(value, ...args);
  }
}

/**
 * Native verbatim ports of the AngularJS `dateRange`/`dateFormat`/`capitalize`
 * $filters (account-v1/.../date-range/{dateRange,dateFormat}.filter.js +
 * text-formatting/capitalize.js) — no longer bridged through $injector.
 * `dateRange`/`dateFormat` format the (deterministic /api) contest dates
 * byte-identically to the frozen baseline; `capitalize` is pure string logic.
 */
@Pipe({ name: 'dateRange' })
export class DateRangePipe implements PipeTransform {
  transform(start: any, end?: any): string {
    const startDate = dayjs.utc(start);
    const endDate = dayjs.utc(end);
    const isSameYear = startDate.year() === endDate.year();
    const startDateFormatted = isSameYear ? startDate.format('MMM D') : startDate.format('MMM D, YYYY');
    const endDateFormatted = endDate.format('MMM D, YYYY');
    return startDateFormatted + ' - ' + endDateFormatted;
  }
}

@Pipe({ name: 'dateFormat' })
export class DateFormatPipe implements PipeTransform {
  transform(date: any, format?: string, isUtc?: boolean): string {
    // v1: isUtc defaults true (angular.isDefined => only `undefined` falls back to true).
    const asUtc = isUtc === undefined ? true : isUtc;
    const parsed = asUtc ? dayjs.utc(date) : dayjs(date);
    return parsed.format(format || 'MMM D');
  }
}

@Pipe({ name: 'capitalize' })
export class CapitalizePipe implements PipeTransform {
  transform(source: any, isSentence?: boolean): any {
    if (Array.isArray(source)) {
      return source
        .filter((element: any) => !!element)
        .map((element: string) => element.toLowerCase())
        .map((element: string) => element[0].toUpperCase() + element.slice(1));
    }
    if (!source) return '';
    const lowerCase = isSentence ? source : source.toLowerCase();
    return lowerCase[0].toUpperCase() + lowerCase.slice(1);
  }
}
